import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { AlaricWidget } from "@/components/site/AlaricWidget";

const POOL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_327707c4-c57f-40e3-bfb7-c75b023b7589/artifacts/0ff837c265136ec4_WhatsApp%20Image%202026-06-01%20at%2020.52.19.jpeg";

const LINES = ["The island keeps", "its own time.", "So will you."];

export const Hero = () => {
  const { settings } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const slides = useMemo(() => {
    const s = settings?.hero_slides;
    return Array.isArray(s) && s.length > 0 ? s : [{ type: "image", url: POOL }];
  }, [settings]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [slides.length]);

  const slide = slides[idx % slides.length];

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] flex flex-col overflow-hidden bg-ink">
      <div className="absolute inset-0">
        <motion.div style={{ y: yBg }} className="absolute inset-0">
          <AnimatePresence>
            <motion.div
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              {slide.type === "video" ? (
                <video
                  src={slide.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="hero-video-slide"
                />
              ) : (
                <img
                  src={slide.url}
                  alt={slide.alt || "Mahadinusa Lembongan — boutique island stay, flowering pool garden"}
                  className="w-full h-full object-cover"
                  data-testid="hero-image-slide"
                />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/20 to-ink/75" />
      </div>

      {slides.length > 1 && (
        <div className="absolute bottom-32 sm:bottom-28 right-5 sm:right-8 z-10 flex gap-1.5" data-testid="hero-slide-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-sand" : "w-1.5 bg-sand/40"}`}
            />
          ))}
        </div>
      )}

      <div className="relative flex-1 w-full max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-36 flex items-center">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-xs uppercase tracking-[0.28em] font-mono text-gold mb-6"
            data-testid="hero-eyebrow"
          >
            Jungutbatu · Nusa Lembongan · Bali
          </motion.p>

          <h1 className="font-serif tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl text-white text-balance" data-testid="hero-heading">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? <em className="not-italic text-sand/90">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 max-w-md text-base sm:text-lg text-white/80 leading-relaxed"
            data-testid="hero-subcopy"
          >
            A small boutique stay wrapped around a flowering pool — garden rooms, slow mornings, and an island
            host who handles the boats, the scooters and the sunsets.
          </motion.p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85, delay: 1.15 }}
        className="relative z-10 w-full max-w-3xl mx-auto px-5 sm:px-8 pb-10"
      >
        <div
          className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-[0_16px_50px_rgba(0,0,0,0.25)] px-2 py-2"
          data-testid="alaric-widget-bar"
        >
          <AlaricWidget />
        </div>
        <p className="mt-2 text-right">
          <a
            href="https://armellabali.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="armella-member-link"
            className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45 hover:text-white/85 transition-colors"
          >
            Member of Armella Bali
          </a>
        </p>
      </motion.div>
    </section>
  );
};
